import {
  AppstoreOutlined,
  BankOutlined,
  CheckCircleOutlined,
  DownOutlined,
  LogoutOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Empty, Input, Modal, Select, Skeleton, Tag, message } from 'antd';
import BrandLogo from 'assets/img/workplansiq-logo.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import useAuthRepo from 'services/repo/useAuthRepo';
import { getSelectedTenant, getTenantMemberships, saveDemoTenantMemberships, saveSelectedTenant } from 'services/tenant/tenantMembershipService';
import LocalStorageUtils from 'utils/LocalStorage';

const STATUS_LABELS = {
  active: 'Active',
  pending: 'Pending',
  'temporary-access': 'Temporary Access',
  provisioning: 'Provisioning',
  expired: 'Expired',
  inactive: 'Inactive',
  suspended: 'Suspended',
};

const isSafeAppPath = (value) =>
  value?.startsWith('/') && !value.startsWith('//');

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const getRemainingDays = (value) => {
  if (!value) return null;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000));
};

const humanizeStatus = (value, fallback = 'Not available') => value
  ? value.toLowerCase().split(/[_-]/).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
  : fallback;

const getActionLabel = (workspace) => {
  if (workspace.status === 'provisioning') return 'View Provisioning';
  if (workspace.status === 'expired') return 'Renew Subscription';
  if (workspace.status === 'suspended') return 'Resolve Billing';
  if (workspace.paymentStatus === 'PENDING_PAYMENT' && workspace.accessStatus !== 'TEMPORARY_ACCESS') return 'Complete Payment';
  return 'Open Organization';
};

const createDemoOrganizations = (currentWorkspace) => {
  const now = Date.now();
  const temporaryAccessEndAt = new Date(now + (9 * 24 * 60 * 60 * 1000)).toISOString();
  const current = {
    ...currentWorkspace,
    paymentStatus: currentWorkspace.paymentStatus || 'PAID',
    accessStatus: currentWorkspace.accessStatus || 'FULL_ACCESS',
    subscriptionStatus: currentWorkspace.subscriptionStatus || 'ACTIVE',
  };

  return [
    current,
    {
      id: 'demo-nusantara-retail',
      name: 'PT Nusantara Retail Indonesia',
      identifier: 'nusantara-retail.worksplaniq.com',
      plan: 'Continuous Workforce Intelligence',
      role: 'Reviewer',
      roleId: '5febd304-7990-4ddd-a9ce-18812aae2a4f',
      status: 'temporary-access',
      subscriptionStatus: 'INVOICE_PENDING',
      paymentStatus: 'PENDING_PAYMENT',
      accessStatus: 'TEMPORARY_ACCESS',
      temporaryAccessEndAt,
      paymentDueAt: temporaryAccessEndAt,
      orgId: 'demo-org-nusantara-retail',
      wcId: 'demo-wc-nusantara-retail',
      source: 'ui-demo',
    },
    {
      id: 'demo-yayasan-cerdas-bangsa',
      name: 'Yayasan Cerdas Bangsa',
      identifier: 'cerdas-bangsa.worksplaniq.com',
      plan: 'Plan not selected',
      role: 'Organization Administrator',
      roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
      status: 'active',
      subscriptionStatus: 'NO_SUBSCRIPTION',
      paymentStatus: 'NOT_STARTED',
      accessStatus: 'EXPLORATION',
      orgId: 'demo-org-cerdas-bangsa',
      wcId: 'demo-wc-cerdas-bangsa',
      source: 'ui-demo',
    },
  ];
};

export default function TenantManagement() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [plan, setPlan] = useState('all');
  const [view, setView] = useState('grid');
  const [joinOpen, setJoinOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinValue, setJoinValue] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuthRepo(false);
  const userInfo = LocalStorageUtils.userInfo.get();
  const activeWorkspace = getSelectedTenant();
  const params = new URLSearchParams(location.search);
  const isManagementMode = params.get('mode') === 'manage';
  const showToolbar = tenants.length > 3;

  useEffect(() => {
    if (LocalStorageUtils.checkTokenExpired()) {
      const returnPath = `${location.pathname}${location.search}`;
      navigate(`/?redirectTo=${encodeURIComponent(returnPath)}`, { replace: true });
      return;
    }

    getTenantMemberships()
      .then((memberships) => {
        const params = new URLSearchParams(location.search);
        const isManagementMode = params.get('mode') === 'manage';
        const continueTo = params.get('continueTo');

        if (!memberships.length) {
          navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`, { replace: true });
          return;
        }
        if (memberships.length === 1 && !isManagementMode) {
          saveSelectedTenant(memberships[0]);
          navigate(
            isSafeAppPath(continueTo) ? continueTo : `${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`,
            { replace: true },
          );
          return;
        }
        const shouldSeedDemoOrganizations = isManagementMode
          && memberships.length === 1
          && memberships[0]?.source === 'onboarding-mock';
        const visibleMemberships = shouldSeedDemoOrganizations
          ? createDemoOrganizations(memberships[0])
          : memberships;
        if (shouldSeedDemoOrganizations) saveDemoTenantMemberships(visibleMemberships);
        setTenants(visibleMemberships);
      })
      .finally(() => setLoading(false));
  }, [location.pathname, location.search, navigate]);

  const plans = useMemo(
    () => [...new Set(tenants.map((tenant) => tenant.plan))],
    [tenants]
  );

  const filteredTenants = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return tenants.filter((tenant) => {
      const matchesPlan = plan === 'all' || tenant.plan === plan;
      const matchesQuery = !normalizedQuery ||
        [tenant.name, tenant.identifier, tenant.id]
          .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));

      return matchesPlan && matchesQuery;
    });
  }, [plan, query, tenants]);

  const openTenant = (tenant) => {
    if (tenant.status === 'provisioning') {
      navigate('/onboarding?resume=provisioning');
      return;
    }
    if (tenant.status === 'expired') {
      navigate('/onboarding?mode=renewal');
      return;
    }
    if (tenant.status === 'suspended') {
      window.location.href = 'mailto:support@worksplaniq.com?subject=Resolve%20organization%20billing';
      return;
    }
    if (tenant.paymentStatus === 'PENDING_PAYMENT' && tenant.accessStatus !== 'TEMPORARY_ACCESS') {
      saveSelectedTenant(tenant);
      navigate(`${APP_ROUTE_PREFIX}/subscription?stage=invoice`);
      return;
    }
    if (tenant.status === 'inactive') return;

    saveSelectedTenant(tenant);
    const continueTo = new URLSearchParams(location.search).get('continueTo');
    const destination = isSafeAppPath(continueTo)
      ? continueTo
      : `${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`;

    navigate(destination, { replace: true });
  };

  const handleLogout = async () => {
    await logout();
    navigate(RouteConstant.login, { replace: true });
  };

  const submitAccessRequest = () => {
    message.success('Access request recorded for this UI demo.');
    setJoinValue('');
    setJoinOpen(false);
  };

  const startOrganizationCreation = () => {
    const timestamp = Date.now();
    const workspace = {
      id: `demo-workspace-${timestamp}`,
      name: 'New Organization Draft',
      identifier: `workspace-${timestamp}`,
      plan: 'Plan not selected',
      role: 'Organization Administrator',
      roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
      status: 'active',
      subscriptionStatus: 'NO_SUBSCRIPTION',
      paymentStatus: 'NOT_STARTED',
      accessStatus: 'EXPLORATION',
      workspaceSetupStatus: 'PENDING',
      workspaceSetupStep: 0,
      workspaceSetupFlowVersion: 2,
      orgId: `demo-org-${timestamp}`,
      wcId: `demo-wc-${timestamp}`,
      source: 'ui-demo',
    };
    const nextTenants = [...tenants, workspace];
    saveDemoTenantMemberships(nextTenants);
    saveSelectedTenant(workspace);
    setCreateOpen(false);
    navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.workspaceSetup}?step=0`);
  };

  return (
    <div className="tenant-selection-page">
      <aside className="tenant-selection-sidebar">
        <img src={BrandLogo} alt="WorkPlansIQ" className="tenant-selection-logo" />
        <div className="tenant-selection-welcome">
          <span>One account</span>
          <strong>Multiple organizations</strong>
          <p>Switch organizations without signing out. Each organization keeps its own plan, billing, and permissions.</p>
        </div>
        <div className="tenant-selection-illustration" aria-hidden="true">
          <BankOutlined />
        </div>
        <div className="tenant-selection-benefits">
          <div><TeamOutlined /><span><strong>Switch anytime</strong>Access all permitted organizations from one account</span></div>
          <div><SafetyCertificateOutlined /><span><strong>Separate billing</strong>Every organization has its own plan and invoice status</span></div>
          <div><CheckCircleOutlined /><span><strong>Role-based access</strong>Your permissions can differ in each organization</span></div>
        </div>
        <p className="tenant-selection-support">Need help? <a href="mailto:support@worksplaniq.com">Contact support</a></p>
      </aside>

      <main className="tenant-selection-main">
        <header className="tenant-selection-topbar">
          <Avatar>{getInitials(userInfo.userFullName || 'User')}</Avatar>
          <span>{userInfo.userFullName || 'WorkPlansIQ User'}</span>
          <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout} aria-label="Logout" />
        </header>

        <section className="tenant-selection-content">
          <div className="tenant-selection-heading">
            <div>
              <span className="tenant-selection-eyebrow">ORGANIZATION MANAGEMENT</span>
              <h1>{isManagementMode ? 'Manage Organizations' : 'Choose an Organization'}</h1>
              <p>{isManagementMode
                ? 'Switch organizations, review your access, or join another organization.'
                : 'Select the organization you want to open.'}</p>
            </div>
            <Button icon={<PlusOutlined />} onClick={() => setJoinOpen(true)}>Join Organization</Button>
          </div>

          <div className="tenant-selection-context-note">
            <SafetyCertificateOutlined />
            <span><strong>Each organization has its own plan and billing.</strong> You can switch organizations without signing in again.</span>
          </div>

          {showToolbar ? <div className="tenant-selection-toolbar">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search organization name, domain, or code..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              allowClear
            />
            <Select
              value={plan}
              onChange={setPlan}
              suffixIcon={<DownOutlined />}
              options={[
                { value: 'all', label: 'All Plans' },
                ...plans.map((value) => ({ value, label: value })),
              ]}
            />
            <div className="tenant-selection-view-toggle" aria-label="View options">
              <Button type={view === 'grid' ? 'primary' : 'text'} icon={<AppstoreOutlined />} onClick={() => setView('grid')} aria-label="Grid view" />
              <Button type={view === 'list' ? 'primary' : 'text'} icon={<UnorderedListOutlined />} onClick={() => setView('list')} aria-label="List view" />
            </div>
          </div> : null}

          {loading ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : filteredTenants.length ? (
            <div className={`tenant-selection-list tenant-selection-list--${view}`}>
              {filteredTenants.map((tenant) => {
                const unavailable = tenant.status === 'inactive';
                const remainingDays = getRemainingDays(tenant.temporaryAccessEndAt || tenant.temporaryAccessEnd);
                const isCurrent = tenant.id === activeWorkspace?.id;
                return (
                  <article className={`tenant-card${isCurrent ? ' tenant-card--current' : ''}`} key={tenant.id}>
                    <div className="tenant-card-header">
                      <span className="tenant-card-icon"><BankOutlined /></span>
                      <div className="tenant-card-tags">
                        {isCurrent ? <Tag color="blue">CURRENT ORGANIZATION</Tag> : null}
                        <Tag className={`tenant-status tenant-status--${tenant.status}`}>{STATUS_LABELS[tenant.status]}</Tag>
                      </div>
                    </div>
                    <h2>{tenant.name}</h2>
                    <dl>
                      <div><dt>Plan</dt><dd>{tenant.plan}</dd></div>
                      <div><dt>Your Role</dt><dd>{tenant.role}</dd></div>
                      <div><dt>Billing</dt><dd>{humanizeStatus(tenant.paymentStatus, 'Not started')}</dd></div>
                      <div><dt>Access</dt><dd>{humanizeStatus(tenant.accessStatus, STATUS_LABELS[tenant.status])}</dd></div>
                      {tenant.accessStatus === 'TEMPORARY_ACCESS' && remainingDays !== null
                        ? <div><dt>Access Ends</dt><dd>{remainingDays} days remaining</dd></div>
                        : null}
                    </dl>
                    <Button block type="primary" disabled={unavailable} onClick={() => openTenant(tenant)}>
                      {unavailable ? 'Unavailable' : isCurrent ? 'Continue to Dashboard' : getActionLabel(tenant).replace('Open', 'Switch to')}
                    </Button>
                  </article>
                );
              })}
              <article className="tenant-card tenant-card--request tenant-card--action">
                <span className="tenant-card-add"><PlusOutlined /></span>
                <h2>Join an Organization</h2>
                <p>Use an invitation code or request access from an organization administrator.</p>
                <Button onClick={() => setJoinOpen(true)}>Request Access</Button>
              </article>
              <article className="tenant-card tenant-card--request tenant-card--action">
                <span className="tenant-card-add"><BankOutlined /></span>
                <h2>Create New Organization</h2>
                <p>Start a new organization with its own plan, billing, members, and data.</p>
                <Button onClick={() => setCreateOpen(true)}>Create Organization</Button>
              </article>
            </div>
          ) : (
            <Empty description="No organizations match your search" />
          )}
        </section>
      </main>

      <Modal title="Join an Existing Organization" open={joinOpen} okText="Send Request" okButtonProps={{ disabled: !joinValue.trim() }} onOk={submitAccessRequest} onCancel={() => setJoinOpen(false)}>
        <p>Enter an invitation code, organization domain, or administrator email.</p>
        <Input value={joinValue} onChange={(event) => setJoinValue(event.target.value)} placeholder="Invitation code or organization domain" />
      </Modal>

      <Modal title="Create a New Organization" open={createOpen} okText="Start Organization Setup" onOk={startOrganizationCreation} onCancel={() => setCreateOpen(false)}>
        <p>Create a new organization with its own structure, members, plan, and billing.</p>
        <div className="tenant-create-coming-soon">
          <Tag color="blue">PROGRESSIVE SETUP</Tag>
          <strong>No subscription plan is required now</strong>
          <span>Your organization starts in Exploration mode. You can choose a plan and complete billing later from the Dashboard.</span>
        </div>
      </Modal>
    </div>
  );
}
